import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2, AlertCircle, Building2, User, LogOut, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';

const countries = [
  'Argentina',
  'Bolivia',
  'Brasil',
  'Chile',
  'Colombia',
  'Ecuador',
  'Paraguay',
  'Perú',
  'Uruguay',
  'Venezuela',
  'México',
  'Estados Unidos',
  'España',
  'Otro'
];

/* ── Custom Country Select (no native OS dropdown) ── */
const CountrySelect = ({ value, onChange, required }: { value: string; onChange: (v: string) => void; required?: boolean }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      {/* Hidden native input for form validation when required */}
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
          value={value}
          onChange={() => {}}
          required
        />
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
      >
        <span className={value ? 'text-brand-black/90' : 'text-neutral-400'}>{value || 'Selecciona tu país'}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 max-h-60 overflow-y-auto">
          {countries.map((country) => (
            <button
              key={country}
              type="button"
              onClick={() => { onChange(country); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-[15px] transition-colors flex items-center justify-between ${
                value === country ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <span>{country}</span>
              {value === country && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Onboarding = () => {
  const navigate = useNavigate();
  // Check URL parameters for initial step
  const urlParams = new URLSearchParams(window.location.search);
  const initialStep = urlParams.get('step') === 'role' ? 'role' : 'personal';
  
  const [step] = useState<'personal' | 'role'>(initialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    pais: ''
  });

  const handleSubmitPersonal = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          name: formData.name,
          pais: formData.pais
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Skip role selection and go directly to owner onboarding
      navigate('/owner-onboarding', { replace: true });
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role: 'owner' | 'employee') => {
    if (role === 'owner') {
      // Bypassing access code page and going directly to owner onboarding
      // with empty access code (temporarily disabled)
      navigate('/owner-onboarding', { replace: true });
    } else {
      navigate('/employee-onboarding');
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/auth');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  if (step === 'personal') {
    return (
      <div className="min-h-screen bg-white md:bg-surface-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md md:max-w-xl">
          {/* Form Card */}
          <div className="bg-white md:bg-surface-card rounded-none md:rounded-lg shadow-none md:shadow-sm px-6 md:p-8">
            <p className="text-[13px] font-medium text-neutral-400 mb-1.5">
              Bienvenido a Atlas
            </p>
            <h2 className="text-[30px] md:text-[22px] font-semibold text-brand-black/90 mb-8 leading-tight">
              Vamos a conocernos<br className="md:hidden" /> <span className="hidden md:inline"></span>¿Cómo te llamas?
            </h2>
            {error && (
              <div className="mb-5 p-3 bg-error-soft border border-error/20 rounded-md text-[13px] text-error-text flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmitPersonal} className="space-y-6">
              <div>
                <label htmlFor="name" className="text-[13px] font-medium text-neutral-600 mb-1 block">
                  Nombre y apellido
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                  placeholder="Tu nombre"
                  required
                />
              </div>

              <div>
                <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
                  País
                </label>
                <CountrySelect
                  value={formData.pais}
                  onChange={(v) => setFormData({ ...formData, pais: v })}
                  required
                />
              </div>

              <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-3 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Continuar'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full md:w-auto h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 text-neutral-600 hover:bg-neutral-100 rounded-md font-medium text-[15px] transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md md:max-w-xl">

        {/* Role Selection Card */}
        <div className="bg-surface-card rounded-lg shadow-sm p-6 md:p-8">
          <h2 className="text-[17px] md:text-[22px] font-semibold text-brand-black/90 mb-1.5">
            ¿Cuál es tu rol?
          </h2>
          <p className="text-[15px] text-neutral-400 mb-6">
            Selecciona tu rol para continuar
          </p>

          <div className="space-y-3">
            <button 
              onClick={() => handleRoleSelect('owner')}
              className="w-full p-4 border border-neutral-200 rounded-md hover:border-brand-primary hover:bg-surface-elevated transition-colors flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-neutral-600" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="text-[15px] font-medium text-brand-black/90">Dueño / Emprendedor</h3>
                <p className="text-[13px] text-neutral-400 break-words">Tengo mi propio negocio o emprendimiento</p>
              </div>
            </button>

            <button 
              onClick={() => handleRoleSelect('employee')}
              className="w-full p-4 border border-neutral-200 rounded-md hover:border-brand-primary hover:bg-surface-elevated transition-colors flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-neutral-600" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="text-[15px] font-medium text-brand-black/90">Empleado</h3>
                <p className="text-[13px] text-neutral-400 break-words">Trabajo para una empresa</p>
              </div>
            </button>
          </div>
          
          {/* Sign Out Button */}
          <div className="mt-6 text-center">
            <button
              onClick={handleSignOut}
              className="text-[13px] text-neutral-400 hover:text-neutral-600 flex items-center gap-1.5 mx-auto transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};